/**
 * Brazil's PROPOSALS for `central.automation` and `ai.spendCaps` (D853 phase B) - country-pack data, NOT seeded and NOT published: the owner publishes them in Admin
 * under four eyes (a change is a new version, never an edit of this file on a running market).
 *
 * The neutral keys live in `@vello/config`. What is Brazilian is the words: the menu options the owner chose on 2026-10-30 (five per audience), the Portuguese words
 * that count as a tap, the question Vello asks for the two provider options that need the person's details, and which of the platform's topics open which subject.
 * The copy follows `docs/design/10_VELLO_GUIA_MESTRE_COPY_E_TOM_DE_VOZ.md`: short, concrete, no promise of a time or an outcome.
 *
 * Intents set to `rules` are answered from the person's own account data in a ready text; `onMiss: human` sends the ones a rule cannot finish to a person with the facts
 * already gathered (no consent question first); `lost_item` and `ride_cancel` miss to the model because the report and the cancellation are confirmed steps of the
 * conversation. Nothing below changes anything until the owner publishes it AND raises the intent above shadow in `central.autonomy`.
 */
import { DEFAULT_CENTRAL_AUTOMATION } from '@vello/config';
const option = (id, intent, pt, en, keywords = [], prompt) => ({
    id, intent, labels: { pt, en }, ...(keywords.length > 0 ? { keywords: { pt: keywords } } : {}), ...(prompt !== undefined ? { prompt } : {}),
});
/** The rider's five options (owner answer 6). */
export const BRAZIL_CUSTOMER_MENU = [
    option('my_ride', 'ride_status', 'Minha corrida', 'My ride', ['cade meu motorista', 'cade o motorista', 'onde esta meu motorista', 'onde esta o motorista', 'status da corrida', 'minha corrida']),
    option('payment_receipt', 'ride_receipt', 'Pagamento ou recibo', 'Payment or receipt', ['recibo', 'comprovante', 'quanto paguei', 'valor da corrida', 'cobranca']),
    option('lost_item', 'lost_item', 'Objeto esquecido', 'Forgotten item', ['esqueci', 'objeto esquecido', 'perdi no carro', 'deixei no carro']),
    option('cancellation', 'ride_cancel', 'Cancelamento', 'Cancellation', ['cancelar corrida', 'cancelar a corrida', 'quero cancelar', 'cancelar a viagem']),
    option('talk', 'human', 'Falar com alguém', 'Talk to someone'),
];
/** The provider's five options (owner answer 6). */
export const BRAZIL_PROVIDER_MENU = [
    option('payout', 'driver_earnings', 'Repasse', 'Payout', ['repasse', 'meu ganho', 'meus ganhos', 'quanto ganhei']),
    option('offer_or_ride', 'faq', 'Oferta ou corrida', 'Offer or ride', [], {
        pt: 'Me conte em uma frase o que aconteceu com a oferta ou com a corrida.',
        en: 'Tell me in one sentence what happened with the offer or the ride.',
    }),
    option('documents', 'kyc_status', 'Documentos', 'Documents', ['documentos', 'meus documentos', 'cadastro']),
    option('account', 'faq', 'Minha conta', 'My account', [], {
        pt: 'Me conte em uma frase o que você precisa na sua conta.',
        en: 'Tell me in one sentence what you need on your account.',
    }),
    option('talk', 'human', 'Falar com alguém', 'Talk to someone'),
];
/** v1 PROPOSAL (not published) of `central.automation` for Brazil. */
export const BRAZIL_CENTRAL_AUTOMATION_V1_PROPOSAL = {
    ...DEFAULT_CENTRAL_AUTOMATION,
    intents: {
        ride_status: { mode: 'rules', onMiss: 'human' },
        ride_receipt: { mode: 'rules', onMiss: 'human' },
        charge_question: { mode: 'rules', onMiss: 'human' },
        ticket_status: { mode: 'rules', onMiss: 'human' },
        driver_earnings: { mode: 'rules', onMiss: 'human' },
        kyc_status: { mode: 'rules', onMiss: 'human' },
        lost_item: { mode: 'rules', onMiss: 'ai' },
        ride_cancel: { mode: 'rules', onMiss: 'ai' },
        faq: { mode: 'rules', onMiss: 'ai' },
        ride_order: { mode: 'ai' },
        refund_request: { mode: 'ai', onMiss: 'human' },
        complaint: { mode: 'ai', onMiss: 'human' },
    },
    menus: { customer: BRAZIL_CUSTOMER_MENU, provider: BRAZIL_PROVIDER_MENU },
    // The site widget's second step: what each topic a visitor taps opens.
    topicIntent: { ride: 'menu', food: 'menu', send: 'menu', payment: 'ride_receipt', lost_item: 'lost_item', trip: 'menu', earnings: 'driver_earnings', document: 'kyc_status' },
    kbDirect: { enabled: true, minScore: 8, minMargin: 3 },
    classifier: { enabled: true, minConfidencePercent: 75, maxChars: 400 },
    generative: { maxTurnsPerConversation: 20, maxToolRounds: 3, minConfidencePercent: 60, historyMessages: 8, passages: 4, maxFailedTurns: 3 },
};
/** v1 PROPOSAL (not published) of `ai.spendCaps` for Brazil: US$ 2 per day (OpenRouter bills in US dollars), at the cap Vello keeps working on rules only (owner answer 7). */
export const BRAZIL_AI_SPEND_CAPS_V1_PROPOSAL = {
    dailyMinor: 200,
    currency: 'USD',
    perPurposePercent: { conversation: 70, classification: 10, summary: 15, translation: 5 },
    atCap: 'rules_only',
};
//# sourceMappingURL=central-automation.js.map